import { GenderEnum } from '@gql/graphql';
import {
  Label,
  MtFormButtons,
  MtFormContainer,
  MtLayouTitleEditButton,
  MtLayouTitleEditButtonContainer,
  MtLayoutTitleWrapper,
  SectionTitle,
} from '@presentation/components';
import { MtLayouTitleBackButton } from '@presentation/components/MtLayout/styled';
import { useTranslation } from '@presentation/utils';
import { Form, useFormikContext } from 'formik';
import type { FC } from 'react';
import { FaArchive, FaChevronLeft, FaEdit, FaTrash } from 'react-icons/fa';
import { MtAnimalBreederForm } from './components/MtAnimalBreederForm';
import { MtStudBreederForm } from './components/MtStudBreederForm';
import { PageSkeleton } from './components/PageSkeleton';
import type { MtAnimalBreederPageProps, MtAnimalsBreederPageFormValues } from './types';

export * from './types';

export const MtAnimalBreederPage: FC<MtAnimalBreederPageProps> = (props) => {
  const {
    mode,
    onCancelClick,
    onEditClick,
    onDeleteClick,
    onBackClick,
    archived = false,
    onUnarchiveClick,
    loading = false,
    onSubmitClick,
  } = props;
  const { translate } = useTranslation();

  const { resetForm, isSubmitting, values } = useFormikContext<MtAnimalsBreederPageFormValues>();

  return (
    <MtFormContainer>
      {loading && <PageSkeleton />}
      {!loading && (
        <Form>
          {!!onBackClick && (
            <MtLayouTitleBackButton onClick={onBackClick}>
              <FaChevronLeft />
              <div>{translate('common.buttons.back')}</div>
            </MtLayouTitleBackButton>
          )}

          <MtLayoutTitleWrapper>
            <SectionTitle>{mode === 'new' && translate('common.buttons.addStud')}</SectionTitle>
            <MtLayouTitleEditButtonContainer>
              {archived && (
                <>
                  <MtLayouTitleEditButton onClick={onUnarchiveClick}>
                    <span>{translate('common.buttons.unarchive')}</span>
                    <FaArchive />
                  </MtLayouTitleEditButton>
                </>
              )}
              {!archived && mode === 'view' && (
                <>
                  <MtLayouTitleEditButton onClick={onEditClick}>
                    <span>{translate('common.buttons.edit')}</span>
                    <FaEdit />
                  </MtLayouTitleEditButton>
                  <MtLayouTitleEditButton onClick={onDeleteClick} color="red">
                    <span>{translate('common.buttons.archive')}</span>
                    <FaTrash />
                  </MtLayouTitleEditButton>
                </>
              )}

              {!archived && mode === 'view' && values.gender === GenderEnum.M && (
                <Label
                  theme={values.studInfo?.availableForBreeding ? 'green' : 'yellow'}
                  content={
                    values.studInfo?.availableForBreeding
                      ? translate('glossary.available')
                      : translate('glossary.unavailable')
                  }
                  tooltip={
                    values.studInfo?.availableForBreeding
                      ? translate('mt.breeder.animals.studAvailable')
                      : translate('mt.breeder.animals.studUnavailable')
                  }
                />
              )}
            </MtLayouTitleEditButtonContainer>
          </MtLayoutTitleWrapper>

          <MtAnimalBreederForm {...props} />
          {values.gender === GenderEnum.M && values.dogInfo?.breederIsDogOwner && (
            <MtStudBreederForm {...props} />
          )}

          <MtFormButtons
            showCancel={mode !== 'view'}
            showSubmit={mode !== 'view'}
            onSubmitClick={onSubmitClick}
            submitLoading={isSubmitting}
            onCancelClick={(event) => {
              resetForm();
              onCancelClick?.(event);
            }}
          />
        </Form>
      )}
    </MtFormContainer>
  );
};
